import React, { useState, useEffect, useRef } from 'react';
import './ModernFormLanding.css';
import { transliterate } from './transliterator';

// Floating, draggable Odia keyboard
function FloatingKeyboard({ visible, onClose, pressedKey, pressedShiftedKey }) {
  const [pos, setPos] = useState({ x: 80, y: 120 });
  const [dragging, setDragging] = useState(false);
  const offset = useRef({ x: 0, y: 0 });

  function onMouseDown(e) {
    setDragging(true);
    offset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y,
    };
    document.body.style.userSelect = 'none';
  }

  const onMouseMove = React.useCallback((e) => {
    if (dragging) {
      setPos({
        x: e.clientX - offset.current.x,
        y: e.clientY - offset.current.y,
      });
    }
  }, [dragging]);

  function onMouseUp() {
    setDragging(false);
    document.body.style.userSelect = '';
  }

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    } else {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragging, onMouseMove]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        zIndex: 9999,
        background: 'linear-gradient(180deg, #b2e0f7 0%, #e6f6ff 100%)',
        border: '2px solid #1a7f3c',
        borderRadius: 10,
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        minWidth: 420,
        minHeight: 120,
        userSelect: 'none',
        padding: 0,
      }}
    >
      <div
        style={{
          background: '#2a9df4',
          color: 'white',
          padding: '0.4rem 1rem',
          cursor: 'move',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontWeight: 600,
          fontSize: '1.1rem',
        }}
        onMouseDown={onMouseDown}
      >
        <span>Odia Keyboard</span>
        <button
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: '1.2rem',
            cursor: 'pointer',
            marginLeft: 8,
          }}
          onClick={onClose}
          title="Close"
        >
          ×
        </button>
      </div>
      <div style={{ padding: '0.5rem 0.5rem 0.5rem 0.5rem', background: '#fafdff', borderRadius: 8 }}>
        <OdiaKeyboard pressedKey={pressedKey} pressedShiftedKey={pressedShiftedKey} />
      </div>
    </div>
  );
}

const ODIA_ALPHABETS = [
  'ଅ','ଆ','ଇ','ଈ','ଉ','ଊ','ଋ','ଏ','ଐ','ଓ','ଔ',
  'କ','ଖ','ଗ','ଘ','ଙ',
  'ଚ','ଛ','ଜ','ଝ','ଞ',
  'ଟ','ଠ','ଡ','ଢ','ଣ',
  'ତ','ଥ','ଦ','ଧ','ନ',
  'ପ','ଫ','ବ','ଭ','ମ',
  'ଯ','ର','ଲ','ଳ','ଵ','ଶ','ଷ','ସ','ହ',
  'କ୍ଷ','ତ୍ର','ଜ୍ଞ',
  'ଂ','ଃ','ଁ',
  '୦','୧','୨','୩','୪','୫','୬','୭','୮','୯'
];

// Full 104-key QWERTY layout with four values per key: en, enShift, od, odShift
const ODIA_KEYBOARD_LAYOUT = [
  // Row 1: Number row
  [
    { en: '`', enShift: '', od: 'ୌ', odShift: 'ତ୍ତ' }, { en: '1', enShift: '', od: '!', odShift: '୧' }, { en: '2', enShift: '', od: '"', odShift: '୨' }, { en: '3', enShift: '', od: 'ଐ', odShift: '୩' }, { en: '4', enShift: '', od: 'ଋ', odShift: '୪' },
    { en: '5', enShift: '', od: '%', odShift: '୫' }, { en: '6', enShift: '', od: 'ଗ୍ଭ', odShift: '୬' }, { en: '7', enShift: '', od: 'ଊ', odShift: '୭' }, { en: '8', enShift: '', od: 'ଈ', odShift: '୮' }, { en: '9', enShift: '', od: '(', odShift: '୯' },
    { en: '0', enShift: '', od: ')', odShift: '୦' }, { en: '-', enShift: '', od: '_', odShift: '-' }, { en: '=', enShift: '', od: '=', odShift: '+' }, { en: '', enShift: '', od: '', odShift: 'BackS' }
  ],
  // Row 2: QWERTY
  [
    { en: '', enShift: '', od: '', odShift: 'Tab' },
    { en: 'Q', enShift: '', od: 'ଠ', odShift: 'ୋ' }, { en: 'W', enShift: '', od: '୍ର', odShift: '୍ୱ' }, { en: 'E', enShift: '', od: 'ଏ', odShift: 'େ' }, { en: 'R', enShift: '', od: 'ୃ', odShift: 'ର' }, { en: 'T', enShift: '', od: 'ଟ', odShift: 'ତ' },
    { en: 'Y', enShift: '', od: ' ୍ୟ', odShift: 'ୟ' }, { en: 'U', enShift: '', od: 'ଉ', odShift: 'ୁ+ୂ' }, { en: 'I', enShift: '', od: 'ଇ', odShift: 'ି+ୀ' }, { en: 'O', enShift: '', od: 'ଔ', odShift: 'ଓ' }, { en: 'P', enShift: '', od: 'ଫ', odShift: 'ପ' },
    { en: '[', enShift: '', od: 'ଡ଼', odShift: 'ଡ' }, { en: ']', enShift: '', od: 'ଢ଼', odShift: 'ଢ' }, { en: '\\', enShift: '', od: 'ୖ', odShift: '୰' }
  ],
  // Row 3: ASDF
  [
    { en: '', enShift: '', od: '', odShift: 'Cap' },
    { en: 'A', enShift: '', od: 'ଆ', odShift: 'ଅ' }, { en: 'S', enShift: '', od: 'ଶ', odShift: 'ସ' }, { en: 'D', enShift: '', od: 'ଧ', odShift: 'ଦ' }, { en: 'F', enShift: '', od: 'ଥ', odShift: ' ୍' }, { en: 'G', enShift: '', od: 'ଘ', odShift: 'ଗ' },
    { en: 'H', enShift: '', od: 'ଃ', odShift: 'ହ' }, { en: 'J', enShift: '', od: 'ଝ', odShift: 'ଜ' }, { en: 'K', enShift: '', od: 'ଖ', odShift: 'କ' }, { en: 'L', enShift: '', od: 'ଲ', odShift: 'ଳ' },
    { en: ';', enShift: '', od: ':', odShift: '.' }, { en: '\'', enShift: '', od: 'ଁ', odShift: '"' }, { en: '', enShift: '', od: '', odShift: 'Enter' }
  ],
  // Row 4: ZXCV
  [
    { en: '', enShift: '', od: '', odShift: 'Shift' },
    { en: 'Z', enShift: '', od: 'ଙ', odShift: 'ଞ' }, { en: 'X', enShift: '', od: 'କ୍ଷ', odShift: 'ଷ' }, { en: 'C', enShift: '', od: 'ଛ', odShift: 'ଚ' }, { en: 'V', enShift: '', od: 'ଞ୍ଚ', odShift: 'ଯ' }, { en: 'B', enShift: '', od: 'ଭ', odShift: 'ବ' },
    { en: 'N', enShift: '', od: 'ଣ', odShift: 'ନ' }, { en: 'M', enShift: '', od: 'ଂ', odShift: 'ମ' },
    { en: ',', enShift: '', od: '<', odShift: ',' }, { en: '.', enShift: '', od: '>', odShift: '।' }, { en: '/', enShift: '', od: '?', odShift: '/' }, { en: '', enShift: '', od: '', odShift: 'Shift' }
  ],
  // Row 5: Only Space button, wider
  [
    { en: '', enShift: '', od: '', odShift: 'SPACE', wide: true }
  ]
];

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function AnimatedOdiaAlphabet() {
  const [letters, setLetters] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLetters((prev) => {
        // Add a new random letter
        const newLetter = {
          char: ODIA_ALPHABETS[getRandomInt(0, ODIA_ALPHABETS.length - 1)],
          id: Math.random().toString(36).substr(2, 9),
          left: getRandomInt(5, 90),
          top: getRandomInt(5, 90),
          size: getRandomInt(2, 5), // rem
          duration: getRandomInt(2500, 4000),
        };
        // Limit to 18 at a time
        return [...prev.slice(-17), newLetter];
      });
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="mf-bg-odia-letters">
      {letters.map((l) => (
        <span
          key={l.id}
          className="mf-bg-odia-letter"
          style={{
            left: `${l.left}%`,
            top: `${l.top}%`,
            fontSize: `${l.size}rem`,
            animationDuration: `${l.duration}ms`,
          }}
        >
          {l.char}
        </span>
      ))}
    </div>
  );
}

// Sticky header with three buttons
function StickyHeader({ onToggleKeyboard, isKeyboardVisible }) {
  return (
    <header className="mf-header mf-header-sticky" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
      <div className="mf-header-odia-typewriter" style={{flex: 1, display: 'flex', justifyContent: 'flex-start'}}>
        <span className="mf-header-title">ଓଡ଼ିଆ-ଭାରି ବଢ଼ିଆ</span>
      </div>
      <div className="mf-header-right-group" style={{display: 'flex', gap: '1rem'}}>
        <button className="mf-header-btn" onClick={onToggleKeyboard}>
          ଓଡ଼ିଆ KEYBOARD {isKeyboardVisible ? '▲' : '▼'}
        </button>
<a 
  href="https://www.youtube.com/@ODIATYPINGTOOL" 
  target="_blank" 
  rel="noopener noreferrer"
>
        <button className="mf-header-btn">YouTube</button>
        </a>

    <a 
  href="/odiatypingtool/FAQ.pdf" 
  target="_blank" 
  rel="noopener noreferrer"
>
        <button className="mf-header-btn">FAQ</button>
</a>

      </div>
    </header>
  );
}

function OdiaKeyboard({ pressedKey, pressedShiftedKey }) {
  return (
    <div className="mf-odia-keyboard">
      {ODIA_KEYBOARD_LAYOUT.map((row, i) => (
        <div className="mf-odia-keyboard-row" key={i}>
          {row.map((keyObj) => {
            // List of Shift+key combos to animate deep red
            const shiftRedKeys = [
              'A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z',
              '1','2','3','4','5','6','7','8','9','0','=','#','`','[',']',',','.','/'
            ];
            const isShifted = pressedShiftedKey && pressedShiftedKey === keyObj.en && shiftRedKeys.includes(keyObj.en);
            const isPressed = pressedKey && pressedKey.toUpperCase() === keyObj.en && !isShifted;
            const isFKey = keyObj.en === 'F';
            return (
              <div
                key={keyObj.en + keyObj.odShift}
                className={
                  'mf-odia-keyboard-key' +
                  (keyObj.wide ? ' mf-odia-keyboard-key-wide' : '') +
                  (isFKey ? ' mf-odia-keyboard-key-blink-heart' : '') +
                  (isShifted
                    ? ' mf-odia-keyboard-key-pressed-shift-a'
                    : isPressed
                    ? ' mf-odia-keyboard-key-pressed'
                    : '')
                }
              >
                <div className="mf-odia-keyboard-odia">{keyObj.od}</div>
                <div className="mf-odia-keyboard-en">{keyObj.en}</div>
                <div className="mf-odia-keyboard-odia-shift">{keyObj.odShift}</div>
                <div className="mf-odia-keyboard-en-shift">{keyObj.enShift}</div>
                {(isPressed || isShifted) && (
                  <span className={isShifted ? "mf-odia-keyboard-key-anim mf-odia-keyboard-key-pressed-shift-a" : "mf-odia-keyboard-key-anim"}>{keyObj.od}</span>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function ModernFormLanding({ specialWords }) {
  const [englishText, setEnglishText] = useState('');
  const [odiaText, setOdiaText] = useState('');
  const [pressedOdiaKey, setPressedOdiaKey] = useState(null);
  const [pressedShiftedKey, setPressedShiftedKey] = useState(null);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  // Handle textarea input and transliterate in real time
  const handleInputChange = (e) => {
    const text = e.target.value;
    setEnglishText(text);
    setOdiaText(transliterate(text, specialWords));
  };

  // Listen for Odia key presses in textarea
  function handleOdiaInputKeyDown(e) {
    const shiftRedKeys = [
      'A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z',
      '1','2','3','4','5','6','7','8','9','0','=','#','`','[',']',',','.','/'
    ];
    if (e.shiftKey && e.key.length === 1 && shiftRedKeys.includes(e.key.toUpperCase())) {
      setPressedShiftedKey(e.key.toUpperCase());
      setTimeout(() => setPressedShiftedKey(null), 300);
      return;
    }
    const char = e.key;
    for (const row of ODIA_KEYBOARD_LAYOUT) {
      for (const keyObj of row) {
        if (keyObj.en.toLowerCase() === char.toLowerCase() || keyObj.en === char) {
          setPressedOdiaKey(keyObj.en);
          setTimeout(() => setPressedOdiaKey(null), 300);
          return;
        }
      }
    }
  }

  function handleOdiaInputKeyUp(e) {
    const shiftRedKeys = [
      'A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z',
      '1','2','3','4','5','6','7','8','9','0','=','#','`','[',']',',','.','/'
    ];
    if (e.shiftKey && e.key.length === 1 && shiftRedKeys.includes(e.key.toUpperCase())) {
      setPressedShiftedKey(null);
    }
  }

  return (
    <div className="mf-root">
      <AnimatedOdiaAlphabet />
      <StickyHeader
        onToggleKeyboard={() => setIsKeyboardVisible((v) => !v)}
        isKeyboardVisible={isKeyboardVisible}
      />
      <div className="mf-main-content" style={{marginTop: 0, paddingTop: 0, minHeight: 0}}>
        <section className="mf-section-card" style={{marginTop: 0, paddingTop: 0}}>
          <div className="mf-section-content mf-two-col" style={{flexDirection: 'column', gap: '2rem'}}>
            <div className="mf-input-tool-block">
              <textarea
                id="odia-english-input"
                value={englishText}
                onChange={handleInputChange}
                placeholder="Type in English...(Ex: Phonetic=>[kandhamal=କନ୍ଧମାଳ] +  Virtual=>[k+nfD+m+a+l=କନ୍ଧମାଳ])"
                rows={3}
                className="mf-input-tool-textarea"
                spellCheck={false}
                onKeyDown={handleOdiaInputKeyDown}
                onKeyUp={handleOdiaInputKeyUp}
                style={{width: '100%', minHeight: '70px', fontSize: '1.13rem', color: '#3d1f1a', background: '#fff', caretColor: '#08fc76bd', zIndex: 2}}
              />
              <label htmlFor="odia-output-area" style={{fontWeight: 600, marginTop: 4, marginBottom: 2, display: 'block', lineHeight: '1.1', paddingBottom: 0}}>Odia Output</label>
              <div
                id="odia-output-area"
                className="mf-output-area"
                style={{
                  width: '100%',
                  minHeight: '70px',
                  color: '#1a7f3c',
                  fontFamily: 'Noto Sans Oriya, Anek Odia, Arial, sans-serif',
                  fontSize: '1.13rem',
                  padding: '0.8rem 1rem',
                  whiteSpace: 'pre-wrap',
                  background: '#e6ffe6',
                  opacity: englishText ? 1 : 0.5,
                  marginBottom: 0
                }}
                readOnly
              >
                {odiaText}
              </div>
              <div className="mf-copy-btn-row">
                <button
                  className="mf-copy-btn"
                  onClick={async () => {
                    if (odiaText) {
                      try {
                        await navigator.clipboard.writeText(odiaText);
                        alert('Odia text copied!');
                      } catch (err) {
                        alert('Copy failed. Please try again.');
                      }
                    }
                  }}
                  disabled={!odiaText}
                >
                  Copy Odia Text
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
      <FloatingKeyboard
        visible={isKeyboardVisible}
        onClose={() => setIsKeyboardVisible(false)}
        pressedKey={pressedOdiaKey}
        pressedShiftedKey={pressedShiftedKey}
      />
      <footer style={{width: '100vw', position: 'sticky', bottom:0, left:0, zIndex: 100, background: 'transparent', margin: 0, padding: 0, display: 'block'}}>
        <div className="mf-copyright-bar" style={{width: '100%', textAlign: 'center', fontSize: '1rem', color: 'white', background: 'linear-gradient(90deg,rgb(70, 134, 199) 0%,rgb(10, 22, 187) 100%)', padding: '0.5rem 0', borderTop: '1px solid #e0e0e0', marginTop: 0}}>
          © 2025- All Rights Reserved, Powered by : VIKRAM NAYAK, Raikia, Kandhamal, Odisha, India-762101.
        </div>
      </footer>
    </div>
  );
}
